import { useState } from 'react';
import { MatchConfirmedContent } from './MatchConfirmedContent';
import { MatchConfirmingContent } from './MatchConfirmingContent';
import { OrgRepConfirmMatchContent } from './OrgRepConfirmMatchContent';
import { MatchDeclinedContent } from './MatchDeclinedContent';
import { MatchDecliningContent } from './MatchDecliningContent';
import { Modal, ModalCallToAction } from '@platform/components';
import { NameCustomProjectContent } from './NameCustomProjectContent';
import * as Sentry from '@sentry/react';
import { OtherUpdateContent } from './OtherUpdateContent';
import { ScheduledContent } from './ScheduledContent';
import { StillDecidingContent } from './StillDecidingContent';
import { MatchedElsewhereContent } from './MatchedElsewhereContent';
import { UnresponsiveContent } from './UnresponsiveContent';
import { UpdateConfirmedContent } from './UpdateConfirmedContent';
import { UpdateSelectionContent } from './UpdateSelectionContent';
import { ProConfirmMatch } from './ProConfirmMatch';
import { useQueryClient } from '@tanstack/react-query';
import {
  SimplifiedUserRole,
  MatchDiscoveryUpdateOutcomeEnum,
  MatchDeclineReasonEnum,
} from '@platform/client/schemas';
import { MyProjects, ProjectHub, Matches } from '@platform/client';
import { navigate } from 'wouter/use-browser-location';
import { ROUTES } from '../../../../../app/routes/consts';
import { useForm } from 'react-hook-form';
import { DiscoveryCallModalStage, DiscoveryModalProps, UpdateDiscoveryCallFormData } from './types';

// Shared enums/types live in ./types to avoid a circular import with the content components
export * from './types';

const toISODate = (date: Date): string => date.toISOString().substring(0, 10);

// Statuses whose fields are collected inline under the dropdown on Update_Selection and submitted
// directly with "Save & continue" — no separate stage, for every user type. MatchedElsewhere is
// handled separately (inline for reps, standalone page for pros); see isInlineStatus below.
const INLINE_STATUSES = new Set<MatchDiscoveryUpdateOutcomeEnum>([
  MatchDiscoveryUpdateOutcomeEnum.scheduled,
  MatchDiscoveryUpdateOutcomeEnum.still_deciding,
  MatchDiscoveryUpdateOutcomeEnum.other,
]);

// Status-specific copy for the shared Update_Confirmed success screen; statuses not listed
// fall back to UpdateConfirmedContent's default message.
const UPDATE_CONFIRMED_MESSAGES: Partial<Record<MatchDiscoveryUpdateOutcomeEnum, string>> = {
  [MatchDiscoveryUpdateOutcomeEnum.still_deciding]:
    'Thanks for the update! No rush, we will check back in with you in a week to see if you’re ready to move forward.',
  [MatchDiscoveryUpdateOutcomeEnum.unresponsive]:
    "Thanks for the update! We'll follow up with you via email if needed.",
};

export const DiscoveryCallUpdateModal = (props: DiscoveryModalProps) => {
  const { matchGroupId, otherPartyName, onClose, userType } = props;

  const { register, setValue, control, trigger, formState, watch } =
    useForm<UpdateDiscoveryCallFormData>({
      mode: 'onChange',
      defaultValues: { requestedMatchingSupport: false },
    });
  const selectedStatus = watch('status');
  const isPro = userType === SimplifiedUserRole.PROFESSIONAL;

  // MatchedElsewhere is inline for reps but gets its own page for pros; the rest are inline for everyone.
  const isInlineStatus = (status: MatchDiscoveryUpdateOutcomeEnum | null | undefined): boolean => {
    if (!status) return false;
    if (status === MatchDiscoveryUpdateOutcomeEnum.matched_elsewhere) return !isPro;
    return INLINE_STATUSES.has(status);
  };

  const selectedReason = watch('reason');
  const declineReasonDetails = watch('declineReasonDetails');
  const otherUpdateDetails = watch('otherUpdateDetails');
  const additionalDetails = watch('additionalDetails');
  const requestedMatchingSupport = watch('requestedMatchingSupport');
  const customProjectName = watch('projectName');

  const [isSubmissionSaving, setIsSubmissionSaving] = useState(false);
  const [modalStage, setModalStage] = useState<DiscoveryCallModalStage>(
    DiscoveryCallModalStage.Update_Selection,
  );
  const [isSubmissionErrored, setIsSubmissionErrored] = useState(false);
  const queryClient = useQueryClient();
  const postDiscoveryUpdateMutation = Matches.usePostDiscoveryUpdate({
    mutation: {
      onMutate: () => {
        setIsSubmissionSaving(true);
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: MyProjects.getGetProUserMyProjectsQueryKey() });
        queryClient.invalidateQueries({ queryKey: MyProjects.getGetOrgUserMyProjectsQueryKey() });
        queryClient.invalidateQueries({ queryKey: MyProjects.getCountUserActionItemsQueryKey() });
        setIsSubmissionSaving(false);
        queryClient.invalidateQueries({
          queryKey: ProjectHub.getGetProjectHubDataByMatchGroupQueryKey(matchGroupId),
        });
      },
      onError: (error) => {
        alert(`An error occurred while submitting the discovery update. ${error}`);
        Sentry.captureException(error);
        setIsSubmissionSaving(false);
      },
    },
  });

  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const [callDate, setCallDate] = useState<Date>(tomorrow);
  const [startDate, setStartDate] = useState<Date>(new Date());
  const initialEndDate = new Date();
  initialEndDate.setDate(initialEndDate.getDate() + 90);
  const [endDate, setEndDate] = useState<Date>(initialEndDate);

  const closeModal = () => {
    setModalStage(DiscoveryCallModalStage.Update_Selection);
    setIsSubmissionErrored(false);
    setCallDate(tomorrow);
    setStartDate(new Date());
    setEndDate(initialEndDate);
    onClose();
  };

  /** Routes mutate() to the right success stage and surfaces an error otherwise. */
  const mutateOptions = (successStage: DiscoveryCallModalStage) => ({
    onSuccess: () => {
      setIsSubmissionErrored(false);
      setModalStage(successStage);
    },
    onError: () => {
      setIsSubmissionErrored(true);
    },
  });

  const submitAcceptedPro = () => {
    postDiscoveryUpdateMutation.mutate(
      {
        matchGroupId,
        data: {
          updateType: 'match_accepted_pro',
          projectStartDate: toISODate(startDate),
          projectEndDate: toISODate(endDate),
          projectName: props.projectName ?? customProjectName,
          additionalDetails: additionalDetails ?? undefined,
        },
      },
      mutateOptions(DiscoveryCallModalStage.Match_Confirmed),
    );
  };

  /** Submits the fields collected inline under the dropdown for an INLINE_STATUSES selection. */
  const submitInlineUpdate = async () => {
    switch (selectedStatus) {
      case MatchDiscoveryUpdateOutcomeEnum.scheduled:
        postDiscoveryUpdateMutation.mutate(
          {
            matchGroupId,
            data: {
              updateType: 'discovery_update',
              outcome: 'scheduled',
              scheduledCallDate: toISODate(callDate),
              additionalDetails: additionalDetails ?? null,
            },
          },
          mutateOptions(DiscoveryCallModalStage.Update_Confirmed),
        );
        break;
      case MatchDiscoveryUpdateOutcomeEnum.still_deciding:
        postDiscoveryUpdateMutation.mutate(
          {
            matchGroupId,
            data: {
              updateType: 'discovery_update',
              outcome: 'still_deciding',
              additionalDetails: additionalDetails ?? null,
            },
          },
          mutateOptions(DiscoveryCallModalStage.Update_Confirmed),
        );
        break;
      case MatchDiscoveryUpdateOutcomeEnum.matched_elsewhere:
        postDiscoveryUpdateMutation.mutate(
          {
            matchGroupId,
            data: {
              updateType: 'match_declined',
              reason: 'other_choice',
              additionalDetails: additionalDetails ?? null,
              requestedSupport: requestedMatchingSupport ?? false,
            },
          },
          mutateOptions(DiscoveryCallModalStage.Update_Confirmed),
        );
        break;
      case MatchDiscoveryUpdateOutcomeEnum.other:
        if (!(await trigger('otherUpdateDetails'))) return;
        postDiscoveryUpdateMutation.mutate(
          {
            matchGroupId,
            data: {
              updateType: 'discovery_update',
              outcome: 'other',
              additionalDetails: otherUpdateDetails ?? '',
            },
          },
          mutateOptions(DiscoveryCallModalStage.Update_Confirmed),
        );
        break;
    }
  };

  const getPrimaryCTA = (): ModalCallToAction => {
    switch (modalStage) {
      case DiscoveryCallModalStage.Update_Selection:
        // Inline statuses collect their fields under the dropdown and submit here; the rest
        // navigate to their own stage.
        if (isInlineStatus(selectedStatus)) {
          return { text: 'Save & continue', callback: submitInlineUpdate };
        }
        return {
          text: 'Continue',
          callback: async () => {
            if (!(await trigger('status'))) return;
            switch (selectedStatus) {
              case MatchDiscoveryUpdateOutcomeEnum.matched:
                // Pros name/date the project; reps just confirm on a single screen
                setModalStage(
                  isPro ?
                    DiscoveryCallModalStage.Match_Confirming
                  : DiscoveryCallModalStage.Confirm_Match,
                );
                break;
              case MatchDiscoveryUpdateOutcomeEnum.not_a_good_fit:
                setModalStage(DiscoveryCallModalStage.Match_Declining);
                break;
              case MatchDiscoveryUpdateOutcomeEnum.unresponsive:
                setModalStage(DiscoveryCallModalStage.Unresponsive);
                break;
              case MatchDiscoveryUpdateOutcomeEnum.matched_elsewhere:
                // Only reached for pros — reps fill this inline above.
                setModalStage(DiscoveryCallModalStage.Matched_Elsewhere);
                break;
            }
          },
        };
      case DiscoveryCallModalStage.Matched_Elsewhere:
        return { text: 'Save & continue', callback: submitInlineUpdate };
      case DiscoveryCallModalStage.Unresponsive:
        return {
          text: 'Save & continue',
          callback: () => {
            postDiscoveryUpdateMutation.mutate(
              {
                matchGroupId,
                data: {
                  updateType: 'discovery_update',
                  outcome: 'unresponsive',
                  additionalDetails: additionalDetails ?? '',
                },
              },
              mutateOptions(DiscoveryCallModalStage.Update_Confirmed),
            );
          },
        };
      case DiscoveryCallModalStage.Match_Declining:
        return {
          text: 'Save & continue',
          callback: async () => {
            // Reason + optional note are collected here; the decline is submitted on the next
            // screen's "Save & close" so the recommendations checkbox is part of the same request.
            if (!(await trigger('reason'))) return;
            setModalStage(DiscoveryCallModalStage.Match_Declined);
          },
        };
      case DiscoveryCallModalStage.Confirm_Match:
        return {
          text: 'Confirm match',
          callback: () => {
            postDiscoveryUpdateMutation.mutate(
              {
                matchGroupId,
                data: {
                  updateType: 'match_accepted_rep',
                  additionalDetails: additionalDetails ?? null,
                },
              },
              mutateOptions(DiscoveryCallModalStage.Match_Confirmed),
            );
          },
        };
      case DiscoveryCallModalStage.Match_Confirming:
        return {
          text: 'Save & continue',
          callback: () => {
            // A custom project (no name yet) needs naming before we can submit the acceptance
            if (!props.projectName) {
              setModalStage(DiscoveryCallModalStage.Custom_Naming);
            } else {
              setModalStage(DiscoveryCallModalStage.Pro_Confirm_Match);
            }
          },
        };
      case DiscoveryCallModalStage.Custom_Naming:
        return {
          text: 'Save & continue',
          callback: async () => {
            if (!(await trigger('projectName'))) return;
            setModalStage(DiscoveryCallModalStage.Pro_Confirm_Match);
          },
        };
      case DiscoveryCallModalStage.Pro_Confirm_Match:
        return {
          text: 'Confirm match',
          callback: () => {
            submitAcceptedPro();
          },
        };
      case DiscoveryCallModalStage.Match_Confirmed:
        return {
          text:
            userType === SimplifiedUserRole.PROFESSIONAL ?
              'Back to Project Hub'
            : 'View in My Projects',
          callback: () => {
            if (userType === SimplifiedUserRole.PROFESSIONAL) {
              closeModal();
            } else {
              navigate(`platform${ROUTES.DASHBOARD}${ROUTES.MY_PROJECTS}`);
            }
          },
        };
      case DiscoveryCallModalStage.Match_Declined:
        return {
          text: 'Save & close',
          callback: () => {
            postDiscoveryUpdateMutation.mutate(
              {
                matchGroupId,
                data: {
                  updateType: 'match_declined',
                  reason: selectedReason as MatchDeclineReasonEnum,
                  additionalDetails: declineReasonDetails ?? null,
                  requestedSupport: requestedMatchingSupport ?? false,
                },
              },
              {
                onSuccess: () => {
                  setIsSubmissionErrored(false);
                  closeModal();
                },
                onError: () => setIsSubmissionErrored(true),
              },
            );
          },
        };
      default:
        return { text: 'Close', callback: closeModal };
    }
  };

  const getSecondaryCTA = (): ModalCallToAction | undefined => {
    switch (modalStage) {
      case DiscoveryCallModalStage.Update_Selection:
        return { text: 'Cancel', callback: closeModal };
      case DiscoveryCallModalStage.Matched_Elsewhere:
      case DiscoveryCallModalStage.Unresponsive:
      case DiscoveryCallModalStage.Match_Declining:
      case DiscoveryCallModalStage.Match_Confirming:
      case DiscoveryCallModalStage.Confirm_Match:
        return {
          text: 'Go back',
          callback: () => setModalStage(DiscoveryCallModalStage.Update_Selection),
        };
      case DiscoveryCallModalStage.Pro_Confirm_Match:
        return {
          text: 'Go back',
          callback: () =>
            setModalStage(
              props.projectName ?
                DiscoveryCallModalStage.Match_Confirming
              : DiscoveryCallModalStage.Custom_Naming,
            ),
        };
      case DiscoveryCallModalStage.Custom_Naming:
        return {
          text: 'Go back',
          callback: () => setModalStage(DiscoveryCallModalStage.Match_Confirming),
        };
      case DiscoveryCallModalStage.Match_Declined:
        return {
          text: 'Go back',
          callback: () => setModalStage(DiscoveryCallModalStage.Match_Declining),
        };
      case DiscoveryCallModalStage.Match_Confirmed:
        return { text: 'Close', callback: closeModal };
      default:
        return undefined;
    }
  };

  const secondaryCTA = getSecondaryCTA();

  return (
    <Modal
      isOpen={true}
      headerDivision='step'
      progress={getProgress(modalStage)}
      onClose={closeModal}
      variant='full-screen'
      title={'Provide a Match Update'}
      primaryCTA={{ ...getPrimaryCTA(), isDisabled: () => isSubmissionSaving }}
      secondaryCTA={
        secondaryCTA ? { ...secondaryCTA, isDisabled: () => isSubmissionSaving } : undefined
      }
    >
      {modalStage === DiscoveryCallModalStage.Update_Selection && (
        <div className='flex flex-col gap-4 items-center'>
          <UpdateSelectionContent
            setValue={setValue}
            selectedStatus={selectedStatus}
            userType={userType}
            formState={formState}
          />
          {selectedStatus === MatchDiscoveryUpdateOutcomeEnum.scheduled && (
            <ScheduledContent
              callDate={callDate}
              setCallDate={setCallDate}
              minDate={tomorrow}
              control={control}
              isSaving={isSubmissionSaving}
            />
          )}
          {selectedStatus === MatchDiscoveryUpdateOutcomeEnum.still_deciding && (
            <StillDecidingContent control={control} isSaving={isSubmissionSaving} />
          )}
          {selectedStatus === MatchDiscoveryUpdateOutcomeEnum.matched_elsewhere && !isPro && (
            <MatchedElsewhereContent control={control} isSaving={isSubmissionSaving} />
          )}
          {selectedStatus === MatchDiscoveryUpdateOutcomeEnum.other && (
            <OtherUpdateContent
              control={control}
              formState={formState}
              isSaving={isSubmissionSaving}
            />
          )}
        </div>
      )}
      {modalStage === DiscoveryCallModalStage.Matched_Elsewhere && (
        <MatchedElsewhereContent control={control} isSaving={isSubmissionSaving} showIntro />
      )}
      {modalStage === DiscoveryCallModalStage.Unresponsive && (
        <UnresponsiveContent
          control={control}
          otherPartyName={otherPartyName}
          isSaving={isSubmissionSaving}
        />
      )}
      {modalStage === DiscoveryCallModalStage.Match_Declining && (
        <MatchDecliningContent
          control={control}
          register={register}
          setValue={setValue}
          formState={formState}
          selectedReason={selectedReason ?? undefined}
          isSaving={isSubmissionSaving}
          userAccountType={userType}
        />
      )}
      {modalStage === DiscoveryCallModalStage.Match_Declined && (
        <MatchDeclinedContent control={control} isSaving={isSubmissionSaving} />
      )}
      {modalStage === DiscoveryCallModalStage.Match_Confirmed && (
        <MatchConfirmedContent otherPartyName={otherPartyName} userAccountType={userType} />
      )}
      {modalStage === DiscoveryCallModalStage.Update_Confirmed && (
        <UpdateConfirmedContent
          message={selectedStatus ? UPDATE_CONFIRMED_MESSAGES[selectedStatus] : undefined}
        />
      )}
      {modalStage === DiscoveryCallModalStage.Confirm_Match && (
        <OrgRepConfirmMatchContent control={control} isSaving={isSubmissionSaving} />
      )}
      {modalStage === DiscoveryCallModalStage.Match_Confirming && (
        <MatchConfirmingContent
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
          isSaving={isSubmissionSaving}
        />
      )}
      {modalStage === DiscoveryCallModalStage.Custom_Naming && (
        <NameCustomProjectContent
          register={register}
          formState={formState}
          isSaving={isSubmissionSaving}
        />
      )}
      {modalStage === DiscoveryCallModalStage.Pro_Confirm_Match && (
        <ProConfirmMatch control={control} formState={formState} isSaving={isSubmissionSaving} />
      )}
      {isSubmissionErrored && (
        <div className='md:absolute md:left-0 md:right-0 text-center mt-12 text-error-500'>
          <p>Something went wrong while submitting your update. Please try again later.</p>
          <p>If the problem persists, please contact support.</p>
        </div>
      )}
    </Modal>
  );
};

const getProgress = (modalState: DiscoveryCallModalStage): number => {
  switch (modalState) {
    case DiscoveryCallModalStage.Update_Selection:
      return 10;
    case DiscoveryCallModalStage.Matched_Elsewhere:
    case DiscoveryCallModalStage.Unresponsive:
    case DiscoveryCallModalStage.Match_Confirming:
    case DiscoveryCallModalStage.Confirm_Match:
    case DiscoveryCallModalStage.Match_Declining:
      return 50;
    case DiscoveryCallModalStage.Custom_Naming:
      return 70;
    case DiscoveryCallModalStage.Match_Confirmed:
    case DiscoveryCallModalStage.Match_Declined:
    case DiscoveryCallModalStage.Update_Confirmed:
      return 100;
    default:
      return 20;
  }
};
